import React from 'react';
import styled, { css } from 'styled-components';
import { remCalc } from '@lux/helpers';

import Anchor from '../Anchor';
import Grid from '../Grid';
import Column from '../Column';
import Row from '../Row';
import Theme from '../../theme';

import Search from './Search';
import Logo from './Logo';

import IconClose from '../../assets/svgs/close.svg';
import IconChevronUp from '../../assets/svgs/chevron-up.svg';
import IconChevronDown from '../../assets/svgs/chevron-down.svg';

const SCHamburgerGrid = styled(Grid)`
  min-width: 100%;
  height: 100vh;
  position: absolute;
  background-color: ${Theme.colours.white};
  top: 0;
  transition: all 400ms ease-out;
  transform: translate(-200px, 0);
  opacity: 0;
  visibility: hidden;
  padding: 0 ${remCalc(15)};

  ${props =>
    props.isHamburgerMenuOpen &&
    css`
      transform: translate(0, 0);
      opacity: 1;
      visibility: visible;
    `};
`;

const SCHamburgerHeader = styled(Row)`
  margin-bottom: ${remCalc(16)};
  min-height: ${remCalc(67)};
`;

const SCMenuCloseIcon = styled(IconClose)`
  cursor: pointer;
  float: right;
  margin-top: ${remCalc(45)};
`;

const SCSearchContainer = styled(Column)`
  padding: ${remCalc(10)};
`;

const SCColumnPrimaryMenuItem = styled(Column)`
  padding-left: 0;
`;

const SCColumnSubMenuItem = styled(SCColumnPrimaryMenuItem)`
  padding-left: ${remCalc(10)};
  margin-top: ${remCalc(10)};
`;

const SCAnchorBlack = styled(Anchor)`
  color: ${Theme.colours.black};
  font-size: ${Theme.fonts.sizes.small};
  display: block;

  &:not([disabled]) {
    &:hover {
      color: ${Theme.colours.black};
    }
  }

  ${props =>
    props.active &&
    css`
      color: ${Theme.colours.singtelred};

      &:not([disabled]) {
        &:hover {
          color: ${Theme.colours.singtelred};
        }
      }
    `};

  & svg {
    float: right;
  }
`;

const SCColumnSubSubMenuItem = styled(Column)`
  margin-top: ${remCalc(10)};
  padding-left: ${remCalc(30)};
`;

const Divider = styled.div`
  margin: ${remCalc(16)} ${remCalc(-24)};
  border-bottom: 1px solid ${Theme.colours.grey_e2};
`;

const Menu = ({
  menuItems,
  activeSubMenuItem,
  activeMenuItem,
  onMenuItemClick,
  firstLevelLinks
}) => {
  return (firstLevelLinks || []).map((menuItem, menuKey) => {
    return (
      <Row key={menuKey} middle="sm">
        {/* First level menu items */}
        <SCColumnPrimaryMenuItem sm={12}>
          <SCAnchorBlack href={menuItem.href}>{menuItem.title}</SCAnchorBlack>
          <Divider />
        </SCColumnPrimaryMenuItem>
        {/* Second level menu items */}
        {menuItem.active &&
          (menuItems || []).map((subMenuItem, subKey) => {
            return (
              <Column key={subKey} sm={12}>
                <SCColumnSubMenuItem sm={12}>
                  {/* We display accordion style if submenus exists */}
                  {(subMenuItem.subMenu || []).length > 0 && (
                    <SCAnchorBlack
                      secondary
                      active={subKey === activeSubMenuItem}
                      onClick={() => onMenuItemClick(menuKey, subKey)}
                    >
                      {subMenuItem.item.title}
                      {subKey === activeSubMenuItem && (
                        <IconChevronUp sm={24} />
                      )}
                      {subKey !== activeSubMenuItem && (
                        <IconChevronDown sm={24} />
                      )}
                    </SCAnchorBlack>
                  )}
                  {/* If no submenu is found, its a html href link */}
                  {(subMenuItem.subMenu || []).length === 0 && (
                    <SCAnchorBlack secondary href={subMenuItem.item.href}>
                      {subMenuItem.item.title}
                    </SCAnchorBlack>
                  )}
                </SCColumnSubMenuItem>
                {/* Third level menu items */}
                {menuKey === activeMenuItem &&
                  subKey === activeSubMenuItem &&
                  subMenuItem.subMenu.map((subSubMenuItem, subSubKey) => {
                    return (
                      <SCColumnSubSubMenuItem key={subSubKey} sm={12}>
                        <SCAnchorBlack
                          secondary
                          href={subSubMenuItem.item.href}
                        >
                          {subSubMenuItem.item.title}
                        </SCAnchorBlack>
                      </SCColumnSubSubMenuItem>
                    );
                  })}
                <Divider />
              </Column>
            );
          })}
      </Row>
    );
  });
};

const MobileMenu = ({
  menuItems,
  isHamburgerMenuOpen,
  logoLink,
  logoAlt,
  activeSubMenuItem,
  activeMenuItem,
  onClose,
  onMenuItemClick,
  firstLevelLinks
}) => (
  <SCHamburgerGrid isHamburgerMenuOpen={isHamburgerMenuOpen}>
    {/* The hamburger menu header */}
    <SCHamburgerHeader position="center">
      {/* Empty column for spacing */}
      <Column sm={4} />
      {/* Logo in the middle */}
      <Column sm={4}>
        <Logo link={logoLink} alt={logoAlt} padded={true} />
      </Column>
      {/* The hamburger close icon */}
      <Column sm={4}>
        <SCMenuCloseIcon onClick={onClose} />
      </Column>
    </SCHamburgerHeader>
    <Row>
      <SCSearchContainer sm={12}>
        <Search isActive={true} />
      </SCSearchContainer>
    </Row>
    <Row>
      <Column sm={12}>
        {/* Loop through all menu items */}
        <Menu
          firstLevelLinks={firstLevelLinks}
          menuItems={menuItems}
          activeSubMenuItem={activeSubMenuItem}
          activeMenuItem={activeMenuItem}
          onMenuItemClick={onMenuItemClick}
        />
      </Column>
    </Row>
  </SCHamburgerGrid>
);

export default MobileMenu;
